import type { IxdDlView } from "@/apis/compare/types";
import { isValueCell, parseCellId } from "@/utils/ixd-cell-model";

/**
 * IXD 표가 3개월·누적 열로 나뉘어 그려졌는지. 값이 아니라 표 구성(값 셀 id)으로 본다 —
 * 값이 빈 칸도 열은 있으니 워크북 isThreeMonthAcc("그렇게 그려졌다")와 같은 기준이다.
 * 셀 id 의 누적 구분 칸은 비어 있거나 3개월·누적 값이라, 비어 있지 않으면 나뉜 표다.
 */
const isIxdTableThreeMonthAcc = (table: IxdDlView["table"]): boolean =>
  Object.values(table ?? {}).some((rows) =>
    rows.some((row) =>
      row.columns.some(
        (column) =>
          isValueCell(column) && parseCellId(column.id).accumulatedType !== "",
      ),
    ),
  );

/** 양쪽 '3개월/누적 사용' 상태. 워크북이 기준이고 IXD가 다르면 IXD를 맞춘다. */
export interface ThreeMonthAccComparison {
  workbook: boolean;
  ixd: boolean;
}

/**
 * 워크북 칸과 IXD 칸에 '3개월/누적 사용' 여부를 나란히 그릴지와 그 상태. 그리지 않으면 null.
 *
 * - 둘 다 안 쓰면: 대부분의 표(연간 보고서 전부 포함)라 그리지 않는다 — 모든 표에 붙으면
 *   정작 다른 표가 묻힌다. 한쪽이라도 쓰면 맞는 표도 그려 맞다는 것을 보인다.
 * - 워크북 표가 없으면(undefined — 아직 불러오지 않았거나 매칭된 표가 없음): 비교할 대상이
 *   없으니 그리지 않는다.
 * - IXD 표가 없으면(문장 등 표가 아닌 블록): 열 구성이 없으니 그리지 않는다.
 */
export const getThreeMonthAccComparison = (
  workbookIsThreeMonthAcc: boolean | undefined,
  ixdTable: IxdDlView["table"],
): ThreeMonthAccComparison | null => {
  if (workbookIsThreeMonthAcc === undefined || !ixdTable) return null;
  const ixdIsThreeMonthAcc = isIxdTableThreeMonthAcc(ixdTable);
  if (!workbookIsThreeMonthAcc && !ixdIsThreeMonthAcc) return null;
  return { workbook: workbookIsThreeMonthAcc, ixd: ixdIsThreeMonthAcc };
};

/**
 * 양쪽 '3개월/누적 사용'이 어긋나면 맞추는 방법 한 줄, 아니면 null.
 * IXD 편집기 비교표시설정의 체크박스 이름 그대로 말한다 — 사용자가 편집기에서 바로 찾는다.
 */
export const getThreeMonthAccMismatch = (
  comparison: ThreeMonthAccComparison | null,
): string | null => {
  if (!comparison || comparison.workbook === comparison.ixd) return null;
  return comparison.workbook
    ? "'3개월/누적 사용'을 체크해주세요."
    : "'3개월/누적 사용' 체크를 해제해주세요.";
};
